import {
  ACTIVE_OPS_LOGISTICS_BY_ID,
  logisticDefinitionForSnapshot,
} from '../catalog/activeLogisticsCatalog';
import type { OpsFlow, OpsGate, OpsLogisticDefinition } from '../types';

/**
 * Shared SSOT-based completion logic for ops logistics.
 *
 * This is the single source of truth for "is this logistic complete?" used by
 * both the Overview board and the flow detail page, so the same logistics
 * always produce the same readiness status.
 *
 * Source of truth = live canonical values (partner/location/season/squad/derived),
 * NOT taskState checkboxes.
 */

export const ALL_OPS_GATES: ReadonlyArray<OpsGate> = [
  'marketing',
  'scheduling',
  'teacher_assignment',
  'invoicing',
  'parent_comms',
  'other',
];

export type GateStatus = 'complete' | 'incomplete' | 'na';

export type SsotSources = {
  /** Historical snapshot rendering only; never canonical active input. */
  school?: any;
  squad: any;
  billingProfile: any;
  partner?: any;
  location?: any;
  partnerSeason?: any;
  partnerBillingProfile?: any;
  derivedOkByRuleId: Record<string, boolean> | null | undefined;
};

export function logisticKeyFromId(logisticId: string): string {
  return String(logisticId || '').trim().replace(/\./g, '__');
}

export function isLogisticMarkedNotNeeded(flow: OpsFlow | null | undefined, logisticId: string): boolean {
  const id = String(logisticId || '').trim();
  if (!id) return false;
  const key = logisticKeyFromId(id);
  const m: any = (flow as any)?.logisticNotNeededByKey || {};
  return Boolean(m && typeof m === 'object' && m[key]);
}

export function getValueAtPath(obj: any, fieldPath: string): any {
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

/**
 * Pure value-based completion rule.
 *
 * Alignment decision: an explicit per-flow "not needed" mark counts as complete
 * regardless of `def.allowNotApplicable`. This matches the redesign's core goal
 * (a partner that does not need a logistic should not look "behind").
 */
export function isLogisticComplete(
  def: OpsLogisticDefinition,
  value: any,
  derivedOk?: boolean,
  notNeeded?: boolean,
): boolean {
  if (!def) return false;
  if (notNeeded) return true;
  if (def.scope === 'derived') return Boolean(derivedOk);
  if (def.type === 'contact_date') {
    if (!value || typeof value !== 'object') return false;
    const contactId = String((value as any)?.contactId || '').trim();
    const date = String((value as any)?.date || '').trim();
    return Boolean(contactId) && Boolean(date);
  }
  if (def.id === 'squad.firstDayTeacherPacket') {
    const sentAt = (value && typeof value === 'object') ? (value as any).sentAt : null;
    return Number.isFinite(Number(sentAt)) && Number(sentAt) > 0;
  }
  const rule = def.completeness?.kind || 'nonEmpty';
  if (rule === 'exists') {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }
  if (rule === 'minNumber') {
    if (value === undefined || value === null || value === '') return false;
    const rawMin = (def.completeness as any)?.min;
    const min = Number.isFinite(Number(rawMin)) ? Number(rawMin) : 1;
    return Number(value) >= min;
  }
  // nonEmpty default
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value === true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
}

function enrollmentMeetsMin(squad: any): boolean {
  const minEnroll = Number((squad as any)?.minEnroll);
  const seatsTaken = Number((squad as any)?.seatsTaken);
  const fallbackEnrollment = Array.isArray((squad as any)?.studentIds) ? ((squad as any).studentIds as any[]).length : 0;
  const enrollment = Number.isFinite(seatsTaken)
    ? Math.max(Math.max(0, Math.trunc(seatsTaken)), Math.max(0, Math.trunc(fallbackEnrollment)))
    : fallbackEnrollment;
  return Number.isFinite(minEnroll) && minEnroll > 0 ? enrollment >= minEnroll : false;
}

/**
 * Resolve completion for a single logistic id against the SSOT sources.
 * Returns null when the logistic id is not in the catalog.
 */
export function completionForLogisticId(
  flow: OpsFlow,
  logisticId: string,
  sources: SsotSources,
  opts: { historical?: boolean } = {},
): boolean | null {
  const id = String(logisticId || '').trim();
  if (!id) return null;
  const def: any = logisticDefinitionForSnapshot(id, opts.historical === true);
  if (!def) return null;

  const notNeeded = isLogisticMarkedNotNeeded(flow, id);
  if (notNeeded) return true;

  if (def.scope === 'derived' && def.storage?.kind === 'derived') {
    const ruleId = String(def.storage.ruleId || '').trim();
    if (!ruleId) return false;
    const derivedOk = ruleId === 'enrollmentMeetsMin'
      ? enrollmentMeetsMin(sources.squad)
      : Boolean((sources.derivedOkByRuleId || {})[ruleId]);
    return isLogisticComplete(def, undefined, derivedOk, false);
  }

  let value: any;
  if (def.storage?.kind === 'billing_profile') value = getValueAtPath(sources.billingProfile, def.storage.fieldPath);
  else if (def.storage?.kind === 'partner_billing_profile') value = getValueAtPath(sources.partnerBillingProfile, def.storage.fieldPath);
  else if (def.storage?.kind === 'partners') value = getValueAtPath(sources.partner, def.storage.fieldPath);
  else if (def.storage?.kind === 'locations') value = getValueAtPath(sources.location, def.storage.fieldPath);
  else if (def.storage?.kind === 'partner_seasons') value = getValueAtPath(sources.partnerSeason, def.storage.fieldPath);
  else if (def.storage?.kind === 'schools') value = getValueAtPath(sources.school, def.storage.fieldPath);
  else if (def.storage?.kind === 'squads') {
    value = getValueAtPath(sources.squad, def.storage.fieldPath);
    const mainDate = String(sources.squad?.schedule?.mainDate || '').slice(0, 10);
    const isSingleDay = sources.squad?.scheduleKind === 'single_day' || Boolean(mainDate);
    if (isSingleDay && mainDate) {
      if (id === 'squad.schedule.startDate' || id === 'squad.schedule.endDate') {
        value = mainDate;
      } else if (id === 'squad.schedule.dayOfWeek' && !String(value || '').trim()) {
        const match = mainDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
          value = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][
            new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay()
          ];
        }
      }
    }
  }
  return isLogisticComplete(def, value, undefined, false);
}

/**
 * Collect the distinct logistic ids referenced by the flow's active checkpoint
 * tasks (non-superseded) across all applied snapshots.
 */
export function flowReferencedLogisticIds(flow: OpsFlow): string[] {
  const ids = new Set<string>();
  const sources = Array.isArray((flow as any)?.appliedSources) ? ((flow as any).appliedSources as any[]) : [];
  for (const src of sources) {
    const snapshot = (src as any)?.snapshot || null;
    const cps = snapshot && Array.isArray((snapshot as any)?.checkpoints) ? ((snapshot as any).checkpoints as any[]) : [];
    for (const cp of cps) {
      const tasks = Array.isArray(cp?.tasks) ? (cp.tasks as any[]) : [];
      for (const t of tasks) {
        const taskId = String(t?.taskId || '').trim();
        if (taskId) {
          const st = String((flow as any)?.taskState?.[taskId]?.status || 'open');
          if (st === 'superseded') continue;
        }
        const logisticId = String(t?.logisticId || '').trim();
        if (logisticId) ids.add(logisticId);
      }
    }
  }
  return Array.from(ids);
}

/**
 * Roll up completion by gate for the logistics this flow actually tracks.
 * A gate with no relevant logistics is 'na' (not applicable to this flow).
 */
export function gateStatusForFlow(flow: OpsFlow, sources: SsotSources): Record<OpsGate, GateStatus> {
  const totals: Record<OpsGate, { total: number; complete: number }> = ALL_OPS_GATES.reduce((acc, g) => {
    acc[g] = { total: 0, complete: 0 };
    return acc;
  }, {} as Record<OpsGate, { total: number; complete: number }>);

  for (const logisticId of flowReferencedLogisticIds(flow)) {
    const def: any = (ACTIVE_OPS_LOGISTICS_BY_ID as any)[logisticId] || null;
    if (!def) continue;
    const complete = completionForLogisticId(flow, logisticId, sources) === true;
    const gates: OpsGate[] = Array.isArray(def.gates) ? def.gates : [];
    for (const gate of gates) {
      if (!totals[gate]) continue;
      totals[gate].total += 1;
      if (complete) totals[gate].complete += 1;
    }
  }

  return ALL_OPS_GATES.reduce((acc, g) => {
    const { total, complete } = totals[g];
    acc[g] = total === 0 ? 'na' : (complete >= total ? 'complete' : 'incomplete');
    return acc;
  }, {} as Record<OpsGate, GateStatus>);
}
